/**
 * JWT token persistence for web and Capacitor native.
 *
 * Web: localStorage. Reads can be synchronous so the React tree boots
 * authenticated on the first render and there is no flash of the login
 * screen on page reload.
 *
 * Native: `@capacitor/preferences`, which is async-only. On the very first
 * launch the React tree boots unauthenticated for one tick while the
 * hydration effect resolves — the Capacitor splash screen masks this.
 *
 * All writes go through `storage()` so the Phase 2 switch to
 * `@capacitor/preferences` on native is a one-file change (see
 * `app/javascript/platform/storage.ts`).
 */

import { isNative } from './index'
import { storage } from './storage'

const TOKEN_KEY = 'jwt-token'
const REFRESH_TOKEN_KEY = 'jwt-refresh-token'

export interface TokenPair {
  token: string | null
  refreshToken: string | null
}

/**
 * Synchronous bootstrap read used for the initial React state init.
 *
 * On web this reads `localStorage` directly so the first paint can be
 * authenticated. On native it returns `null` — the async `hydrateTokens`
 * effect fills the real values in after mount.
 */
export function bootstrapTokensSync(): TokenPair {
  if (isNative()) return { token: null, refreshToken: null }
  if (typeof window === 'undefined') return { token: null, refreshToken: null }
  try {
    return {
      token: window.localStorage.getItem(TOKEN_KEY),
      refreshToken: window.localStorage.getItem(REFRESH_TOKEN_KEY),
    }
  } catch {
    return { token: null, refreshToken: null }
  }
}

/**
 * Async canonical read. On web this is equivalent to the sync bootstrap
 * (`storage()` is backed by localStorage). On native it reads from
 * `@capacitor/preferences`.
 */
export async function hydrateTokens(): Promise<TokenPair> {
  const s = storage()
  const [token, refreshToken] = await Promise.all([
    s.get(TOKEN_KEY),
    s.get(REFRESH_TOKEN_KEY),
  ])
  return { token, refreshToken }
}

export async function saveTokens(tokens: TokenPair): Promise<void> {
  const s = storage()
  if (tokens.token) {
    await s.set(TOKEN_KEY, tokens.token)
  } else {
    await s.remove(TOKEN_KEY)
  }
  if (tokens.refreshToken) {
    await s.set(REFRESH_TOKEN_KEY, tokens.refreshToken)
  } else {
    await s.remove(REFRESH_TOKEN_KEY)
  }
}

export async function clearTokens(): Promise<void> {
  await saveTokens({ token: null, refreshToken: null })
}
